/**
 * NavigationHandler - Handles modal navigation (click, keyboard, touch)
 *
 * @module NavigationHandler
 */

'use strict';

/**
 * Navigation Handler Class
 */
export class NavigationHandler {
    /**
     * Navigation Handler Class
     */
    constructor(config, onNext, onPrev, onFullscreen) {
        this.config         = config;
        this.onNext         = onNext;
        this.onPrev         = onPrev;
        this.onFullscreen   = onFullscreen;
        this.renderer       = null;
        this.galleryManager = null;
        this.contentUpdater = null;
    }

    /**
     * Set dependencies
     * @param {Object} renderer - MediaRenderer instance
     * @param {Object} galleryManager - Gallery manager instance
     * @param {Object} contentUpdater - ContentUpdater instance
     * @public
     */
    setDependencies(renderer, galleryManager, contentUpdater) {
        this.renderer       = renderer;
        this.galleryManager = galleryManager;
        this.contentUpdater = contentUpdater;
    }

    /**
     * Setup modal navigation
     * @param {HTMLElement} modal - Modal element
     * @param {Object} galleryManager - Gallery manager instance
     * @private
     */
    setup(modal, galleryManager) {
        if (!galleryManager.hasGallery()) {
            return;
        }

        this.setupClickNavigation(modal);
        this.setupKeyboardNavigation(modal, galleryManager);
        this.setupTouchNavigation(modal, galleryManager);
    }

    /**
     * Setup click navigation
     * @param {HTMLElement} modal - Modal element
     * @private
     */
    setupClickNavigation(modal) {
        modal.addEventListener('click', (e) => {
            const navButton = e.target.closest('[data-media-nav]');
            if (navButton && !navButton.disabled) {
                e.preventDefault();
                e.stopPropagation();

                const direction = navButton.dataset.mediaNav;

                if (this.config.debugMode) {
                    console.log('NavigationHandler: Navigation button clicked:', direction);
                }

                if (direction === 'next') {
                    this.onNext();
                } else if (direction === 'prev') {
                    this.onPrev();
                }
                return;
            }

            if (this.config.fullscreen) {
                const fullscreenButton = e.target.closest('[data-media-fullscreen]');
                if (fullscreenButton) {
                    e.preventDefault();
                    e.stopPropagation();

                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Fullscreen button clicked');
                    }

                    if (this.onFullscreen) {
                        this.onFullscreen(modal);
                    }
                }
            }
        });
    }

    /**
     * Setup touch/swipe navigation
     * @param {HTMLElement} modal - Modal element
     * @param {Object} galleryManager - Gallery manager instance
     * @private
     */
    setupTouchNavigation(modal, galleryManager) {
        if (!galleryManager.hasGallery()) {
            return;
        }

        const wrapper = modal.querySelector('.bm-media-wrapper');
        if (!wrapper) {
            return;
        }

        let touchStartX        = 0;
        let touchStartY        = 0;
        let touchEndX          = 0;
        let touchEndY          = 0;
        const minSwipeDistance = 80;

        wrapper.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
            touchStartY = e.changedTouches[0].screenY;
        }, {passive:true});

        wrapper.addEventListener('touchend', (e) => {
            touchEndX = e.changedTouches[0].screenX;
            touchEndY = e.changedTouches[0].screenY;

            const deltaX = touchEndX - touchStartX;
            const deltaY = touchEndY - touchStartY;

            if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
                if (this.config.debugMode) {
                    console.log('NavigationHandler: Swipe detected:', {
                        deltaX,
                        deltaY
                    });
                }

                if (deltaX > 0) {
                    this.onPrev();
                } else {
                    this.onNext();
                }
            }
        }, {passive:true});
    }

    /**
     * Setup keyboard navigation (left/right arrows)
     * @param {HTMLElement} modal - Modal element
     * @param {Object} galleryManager - Gallery manager instance
     * @private
     */
    setupKeyboardNavigation(modal, galleryManager) {
        if (!galleryManager.hasGallery()) {
            return;
        }

        const keyHandler = (e) => {
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Keyboard: ArrowLeft');
                    }
                    this.onPrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Keyboard: ArrowRight');
                    }
                    this.onNext();
                    break;
                case 'Home':
                    e.preventDefault();
                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Keyboard: Home');
                    }
                    this.goToFirst(galleryManager);
                    break;
                case 'End':
                    e.preventDefault();
                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Keyboard: End');
                    }
                    this.goToLast(galleryManager);
                    break;
                case 'PageUp':
                    e.preventDefault();
                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Keyboard: PageUp');
                    }
                    this.navigateBy(galleryManager, -5);
                    break;
                case 'PageDown':
                    e.preventDefault();
                    if (this.config.debugMode) {
                        console.log('NavigationHandler: Keyboard: PageDown');
                    }
                    this.navigateBy(galleryManager, 5);
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', keyHandler);

        modal._keyHandler = keyHandler;
    }

    /**
     * Update navigation buttons state
     * @param {HTMLElement} modal - Modal element
     * @param {Object} galleryManager - Gallery manager instance
     */
    updateButtons(modal, galleryManager) {
        const prevBtn = modal.querySelector('.bm-media-nav__prev');
        const nextBtn = modal.querySelector('.bm-media-nav__next');
        const counter = modal.querySelector('.bm-media-counter .bm-media-counter__current');

        if (prevBtn) {
            prevBtn.disabled = !galleryManager.canGoPrev();
        }

        if (nextBtn) {
            nextBtn.disabled = !galleryManager.canGoNext();
        }

        if (counter) {
            counter.textContent = galleryManager.currentIndex + 1;
        }
    }

    /**
     * Go to first item in gallery
     * @param {Object} galleryManager - Gallery manager
     * @private
     */
    goToFirst(galleryManager) {
        while (galleryManager.currentIndex > 0) {
            const result = galleryManager.prev();
            if (typeof result !== 'boolean') {
                break;
            }
        }
    }

    /**
     * Go to last item in gallery
     * @param {Object} galleryManager - Gallery manager
     * @private
     */
    goToLast(galleryManager) {
        const galleryLength = galleryManager.getGalleryLength();
        while (galleryManager.currentIndex < galleryLength - 1) {
            const result = galleryManager.next();
            if (typeof result !== 'boolean') {
                break;
            }
        }
    }

    /**
     * Navigate by specific number of items
     * @param {Object} galleryManager - Gallery manager
     * @param {number} count - Number of items to navigate (positive or negative)
     * @private
     */
    navigateBy(galleryManager, count) {
        for (let i = 0; i < Math.abs(count); i++) {
            if (count > 0) {
                const result = galleryManager.next();
                if (!result) {
                    break;
                }
            } else {
                const result = galleryManager.prev();
                if (!result) {
                    break;
                }
            }
        }
    }


    /**
     * Cleanup keyboard listener
     * @param {HTMLElement} modal - Modal element
     * @private
     */
    cleanupKeyboard(modal) {
        if (modal && modal._keyHandler) {
            document.removeEventListener('keydown', modal._keyHandler);
            if (this.config.debugMode) {
                console.log('NavigationHandler: Cleaned up keyboard listener');
            }
        }

        const shortcutsEl = modal.querySelector('.bm-keyboard-shortcuts');
        if (shortcutsEl) {
            shortcutsEl.remove();
        }
    }
}

export default NavigationHandler;
